import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronRight, Copy, Lock, Phone } from 'lucide-react';
import type { TeamMemberCardDto } from '../../../../domain/contracts/api.contracts';
import { TIER_DESIGNATION_MAP } from '../../../../domain/enums/TierLevel';
import { useTranslation } from '../../../../application/hooks/useTranslation';
import { Badge } from '../../../components/ui/Badge';
import { initials, isAgent, statusAccent, telHref, tierTone } from '../teamFormat';

export interface MemberCardProps {
  card: TeamMemberCardDto;
  selected?: boolean;
  compact?: boolean;
  onOpen: (card: TeamMemberCardDto) => void;
  onDrill?: (card: TeamMemberCardDto) => void;
}

/** Navigator card: body opens Member 360; the "Team (n) ›" chip drills into the member's level. */
export function MemberCard({ card, selected = false, compact = false, onOpen, onDrill }: MemberCardProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLLIElement>(null);
  const [copied, setCopied] = useState(false);
  const accent = statusAccent(card.status, card.overdueMonths);
  const pct = card.totalInstallments > 0 ? Math.round((card.clearedInstallments / card.totalInstallments) * 100) : 0;

  useEffect(() => {
    if (selected) ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [selected]);

  return (
    <li
      ref={ref}
      className={`tm-card tm-card--${accent}${selected ? ' is-selected' : ''}${compact ? ' is-compact' : ''}`}
      data-testid="team-member-card"
      data-user-id={card.apartmentId}
    >
      <button
        type="button"
        className="tm-card-main"
        onClick={() => onOpen(card)}
        aria-current={selected ? 'true' : undefined}
        aria-label={`${card.fullName}, ${TIER_DESIGNATION_MAP[card.tier]}, ${t(`team.status_${card.status}`)}`}
      >
        <span className={`tm-avatar tm-avatar--${card.tier.toLowerCase()}`} aria-hidden>
          {initials(card.fullName)}
          <span className={`tm-dot tm-dot--${accent}`} />
        </span>
        <span className="tm-card-body">
          <span className="tm-card-name-row">
            <strong className="tm-card-name">{card.fullName}</strong>
            <Badge tone={tierTone(card.tier)}>{card.tier}</Badge>
            {accent === 'closed' && <Lock size={12} className="tm-lock" aria-hidden />}
          </span>
          <span className="tm-card-meta tabular">
            {card.apartmentId}
            {card.phone && <> · {card.phone}</>}
            {isAgent(card.tier) && !compact && (
              <>
                {' · '}
                {t('team.total')}: {card.totalTeamCount}
              </>
            )}
          </span>
          <span className="tm-card-progress">
            <span className="tm-ring" style={{ ['--pct' as string]: `${pct}%` }} aria-hidden />
            <span className="tabular">{card.clearedInstallments}/{card.totalInstallments}</span>
            {card.overdueMonths > 0 && accent !== 'closed' && (
              <Badge tone={accent === 'bad' ? 'danger' : 'warning'}>
                <AlertTriangle size={11} aria-hidden /> {t('team.monthsOverdue', { n: card.overdueMonths })}
              </Badge>
            )}
          </span>
        </span>
      </button>
      <div className="tm-card-actions">
        {card.phone && (
          <>
            <a
              href={telHref(card.phone)}
              className="tm-action-btn tm-call-btn"
              aria-label="Call phone"
              title={`Call ${card.phone}`}
              data-testid="team-call-btn"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone size={13} aria-hidden />
            </a>
            <button
              type="button"
              className="tm-action-btn tm-copy-btn"
              aria-label="Copy phone"
              title="Copy phone"
              data-testid="team-copy-phone-btn"
              onClick={(e) => {
                e.stopPropagation();
                void navigator.clipboard?.writeText(card.phone).catch(() => undefined);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? <Check size={13} color="#059669" /> : <Copy size={13} aria-hidden />}
            </button>
          </>
        )}
        {onDrill && isAgent(card.tier) && (
          <button type="button" className="tm-drill" onClick={() => onDrill(card)} data-testid="team-drill-in">
            {t('team.teamChip', { n: card.directReportsCount })}
            <ChevronRight size={14} aria-hidden />
          </button>
        )}
      </div>
    </li>
  );
}
